import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Data, Params, RouterLink } from '@angular/router';
import { DamageReportState } from '@models/damage-report';
import { PlaceholderCardComponent, PortalWidgetDetailsComponent } from 'app/customer-portal/components';
import { getCanonicalProduct } from 'app/customer-portal/fns/get-canonical-product';
import { DamageReportsService } from '../../damage-reports.service';
import { ContractsService } from 'app/customer-portal/contracts.service';
import { map, switchMap, tap } from 'rxjs';
import { DatePipe } from '@angular/common';
import { CtrlIconComponent } from '@co-3/ctrl-ui/icons';
import { AttachedFileList } from '../../components/attached-file-list/attached-file-list';

@Component({
    selector: 'app-damage-report-details-view',
    standalone: true,
    imports: [
        PortalWidgetDetailsComponent,
        CtrlIconComponent,
        AttachedFileList,
        RouterLink,
        DatePipe,
        PlaceholderCardComponent,
    ],
    templateUrl: './damage-report-details-view.component.html',
    styleUrl: './damage-report-details-view.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DamageReportDetailsViewComponent {
    private route = inject(ActivatedRoute);
    private damageReportsService = inject(DamageReportsService);
    private contractsService = inject(ContractsService);

    protected justSubmitted = toSignal(
        this.route.queryParams.pipe(map((params) => params?.just_submitted === 'true')),
        {
            initialValue: false,
        },
    );

    protected contract = toSignal(
        this.route.params.pipe(
            map((params: Params) => parseInt(params.contractId, 10)),
            switchMap((contractId: number) => this.contractsService.get(contractId)),
        ),
    );

    protected damage = toSignal(this.route.data.pipe(map((data: Data) => data.damageReport)));

    protected attachmentsPending = signal<boolean>(true);

    protected attachments = toSignal(
        this.route.params.pipe(
            tap(() => this.attachmentsPending.set(true)),
            map((params: Params) => parseInt(params.damageId, 10)),
            switchMap((damageId: number) => this.damageReportsService.getAttachments(damageId)),
            tap(() => this.attachmentsPending.set(false)),
        ),
    );

    protected product = getCanonicalProduct(this.contract);

    protected damageState: typeof DamageReportState = DamageReportState;

    protected iconColor = computed(() => {
        switch (this.damage().state) {
            case DamageReportState.COMPLETED:
                return 'c:green';
            case DamageReportState.IN_PROGRESS:
                return 'c:orange';
            case DamageReportState.REPORTED:
                return 'c:blue-700';
        }
    });
}
